import { Language, LanguageMap, Subsidiary, SubsidiaryMap } from '@coolblue-development/constants';
import { WorkArea } from '@graphQLTypes';

import { generateWebsiteUrl } from '../generateWebsiteUrl';
import { legacyBaseUrls } from './legacyBaseUrls';

const baseURL_NL_Dutch = legacyBaseUrls[Subsidiary.NL][Language.DUTCH];
const baseURL_NL_English = legacyBaseUrls[Subsidiary.NL][Language.ENGLISH];
const baseURL_BE_Dutch = legacyBaseUrls[Subsidiary.BE][Language.DUTCH];
const baseURL_BE_French = legacyBaseUrls[Subsidiary.BE][Language.FRENCH];
const baseURL_DE_German = legacyBaseUrls[Subsidiary.DE][Language.GERMAN];

export const makeDisplayableUrl = (url: string) => url.replace('https://www.c', 'C');

const legacyRoutes = {
  webshop: {
    nl: {
      nl: 'https://www.coolblue.nl',
      en: 'https://www.coolblue.nl/en'
    },
    be: {
      nl: 'https://www.coolblue.be/nl',
      fr: 'https://www.coolblue.be/fr',
      en: 'https://www.coolblue.be/en'
    },
    de: {
      de: 'https://www.coolblue.de',
      en: 'https://www.coolblue.de/en'
    }
  },
  base: {
    nl: { nl: baseURL_NL_Dutch, en: baseURL_NL_English },
    be: { nl: baseURL_BE_Dutch, fr: baseURL_BE_French },
    de: { de: baseURL_DE_German }
  },
  'vacancies.overview': {
    nl: {
      nl: `${baseURL_NL_Dutch}/vacatures`,
      en: `${baseURL_NL_English}/vacancies`
    },
    be: {
      nl: `${baseURL_BE_Dutch}/vacatures`,
      fr: `${baseURL_BE_French}/emplois`
    },
    de: {
      de: `${baseURL_DE_German}/jobs`
    }
  },
  culture: {
    nl: {
      nl: `${baseURL_NL_Dutch}/over-coolblue`,
      en: `${baseURL_NL_English}/about-coolblue`
    },
    be: {
      nl: `${baseURL_BE_Dutch}/over-coolblue-be`,
      fr: `${baseURL_BE_French}/a-propos`
    },
    de: {
      de: `${baseURL_DE_German}/kultur`
    }
  },
  events: {
    nl: {
      nl: `${baseURL_NL_Dutch}/events`
    },
    be: {
      nl: `${baseURL_BE_Dutch}/events`
    },
    de: {
      de: `${baseURL_DE_German}/events`
    }
  },
  internships: {
    nl: {
      nl: `${baseURL_NL_Dutch}/stage`
    }
  },
  codemuseum: {
    nl: {
      nl: `${baseURL_NL_Dutch}/codemuseum`
    }
  },
  specialties: {
    nl: {
      nl: `${baseURL_NL_Dutch}/specialismes`,
      en: `${baseURL_NL_English}/specialties`
    },
    be: {
      nl: `${baseURL_BE_Dutch}/specialismes-be`,
      fr: `${baseURL_BE_French}/specialites`
    },
    de: {
      de: `${baseURL_DE_German}/fachgebiete`
    }
  },
  bikeDelivery: {
    nl: {
      nl: `${baseURL_NL_Dutch}/bezorging/coolbluefietst`
    }
  },
  delivery: {
    nl: {
      nl: `${baseURL_NL_Dutch}/bezorging`
    }
  },
  shops: {
    nl: {
      nl: `${baseURL_NL_Dutch}/winkels`
    }
  },
  warehouse: {
    nl: {
      nl: `${baseURL_NL_Dutch}/magazijn`
    }
  },
  relocation: {
    nl: {
      en: `${baseURL_NL_English}/relocation`
    }
  },
  aboutCoolblue: {
    de: {
      de: `${baseURL_DE_German}/ueber-coolblue`
    }
  },
  history: {
    de: {
      de: `${baseURL_DE_German}/geschichte`
    }
  },
  startersJobs: {
    nl: {
      nl: `${baseURL_NL_Dutch}/hoofdkantoor/startersbanen`
    }
  },
  privacy: {
    nl: {
      nl: `${baseURL_NL_Dutch}/privacy`,
      en: `${baseURL_NL_English}/privacy`
    },
    be: {
      nl: `${baseURL_BE_Dutch}/privacy`,
      fr: `${baseURL_BE_French}/privacy`
    },
    de: {
      de: `${baseURL_DE_German}/privacy`
    }
  },
  cookies: {
    nl: {
      nl: `${baseURL_NL_Dutch}/cookies`,
      en: `${baseURL_NL_English}/cookies`
    },
    be: {
      nl: `${baseURL_BE_Dutch}/cookies`,
      fr: `${baseURL_BE_French}/cookies`
    },
    de: {
      de: `${baseURL_DE_German}/cookies`
    }
  },
  affiliate: {
    nl: {
      nl: `https://www.coolblue.nl/c/affiliate-recruitment.html`,
      en: `https://www.coolblue.nl/en/c/affiliate-recruitment.html`
    },
    be: {
      nl: `https://www.coolblue.be/nl/c/affiliate-recruitment.html`,
      fr: `https://www.coolblue.be/fr/c/programme-d-affiliation-pour-le-recrutement.html`
    },
    de: {
      de: `https://www.coolblue.de/c/affiliate-recruitment.html`
    }
  },
  imprint: {
    de: {
      de: `${baseURL_DE_German}/impressum`
    }
  },
  facebook: {
    nl: {
      nl: 'https://www.facebook.com/Coolblue'
    }
  },
  instagram: {
    nl: {
      nl: 'https://www.instagram.com/coolblue'
    },
    be: {
      nl: 'https://www.instagram.com/coolblue_be/',
      fr: 'https://www.instagram.com/coolblue_be_fr/'
    },
    de: {
      de: 'https://www.instagram.com/coolblue_de/'
    }
  },
  tiktok: {
    nl: {
      nl: 'https://www.tiktok.com/@coolblue_nl'
    },
    be: {
      nl: 'https://www.tiktok.com/@coolblue_be',
      fr: 'https://www.tiktok.com/@coolblue_belgique'
    },
    de: {
      de: 'https://www.tiktok.com/@coolblue_de'
    }
  },
  youtube: {
    nl: {
      nl: 'https://www.youtube.com/user/coolblue'
    }
  },
  filterPage: {
    nl: {
      nl: generateWebsiteUrl('zoeken', Subsidiary.NL, Language.DUTCH),
      en: generateWebsiteUrl('search', Subsidiary.NL, Language.ENGLISH)
    },
    be: {
      nl: generateWebsiteUrl('zoeken', Subsidiary.BE, Language.DUTCH),
      fr: generateWebsiteUrl('rechercher', Subsidiary.BE, Language.FRENCH),
      en: generateWebsiteUrl('search', Subsidiary.BE, Language.ENGLISH)
    },
    de: {
      de: generateWebsiteUrl('suchen', Subsidiary.DE, Language.GERMAN),
      en: generateWebsiteUrl('search', Subsidiary.DE, Language.ENGLISH)
    }
  }
} as const;

// Groundwork for when we have specific filter pages for departments
const filterPages: Record<WorkArea, Partial<SubsidiaryMap<Partial<LanguageMap<string>>>>> = {
  [WorkArea.CustomerService]: {
    ...legacyRoutes.filterPage
  },
  [WorkArea.Delivery]: {
    ...legacyRoutes.filterPage
  },
  [WorkArea.Office]: {
    ...legacyRoutes.filterPage
  },
  [WorkArea.Store]: {
    ...legacyRoutes.filterPage
  },
  [WorkArea.Warehouse]: {
    ...legacyRoutes.filterPage
  }
} as const;

export const routes: Record<
  keyof typeof legacyRoutes | keyof typeof filterPages,
  Partial<SubsidiaryMap<Partial<LanguageMap<string>>>>
> = { ...legacyRoutes, ...filterPages };
